import { createSearchParams } from "react-router-dom";

import { MetricLimitMapping } from "config/copies/detectorMetrics";
import { DetectorModes } from "types/createDetector";
import { ANSWER_VALUES } from "types/imageQueries";

import { AnswerType } from "../hooks/DetectorDetail/useTopLevelStats";

export interface FilterParams {
  columnField: string;
  operatorValue: string | null;
  value: string;
  valueSuffix?: string;
}

interface DataPageQueryParams {
  detector_id: string;
  filterModel?: string;
}

export const generateUrlToDataPage = (filtersPage: DataPageQueryParams) =>
  `/reef/data/image-queries?${createSearchParams(filtersPage as any)}`;

export type LabelName =
  | "Answered By ML"
  | "Answered By Cloud Labelers"
  | "Ground Truth"
  | "Total Answered"
  | "Not Answered"
  | "Total Queries";

export const LABEL_SOURCE = {
  "Answered By ML": "algorithm",
  "Answered By Cloud Labelers": "labeler",
  "Ground Truth": "support_or_user",
};

export const SOURCE_KEYS = {
  system_answer: "system_answer_source",
  current_best_answer: "current_best_answer_source",
};

export const flaggedFilter = {
  unsure: {
    current_best_prediction_str: "unclear",
  },
};

const mapObject = (obj: Record<string, string | undefined>) => {
  const arr: FilterParams[] = [];
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined) {
      arr.push({
        columnField: key,
        operatorValue: "=",
        value: obj[key] as string,
      });
    }
  });
  return arr;
};

type DataPageRedirectOptions = {
  detectorId: string;
  filterOptions?: Record<string, string>;
  bestAnswer?: string;
  additionalFilters?: string;
};

export const redirectToDataPage = (props: DataPageRedirectOptions) => {
  const { detectorId, filterOptions, bestAnswer, additionalFilters } = props;
  // Create filter model based on bestAnswer if provided, otherwise based on filterOptions if available.
  let filterModel;
  if (bestAnswer) {
    // If there is a bestAnswer, prioritize using that for the filterModel.
    const currentBestAnswer = mapObject({
      current_best_answer_str: bestAnswer,
    });
    filterModel = JSON.stringify({ items: currentBestAnswer });
  } else if (filterOptions) {
    // If there are filterOptions but no bestAnswer, use filterOptions for the filterModel.
    const systemAnswerArray = mapObject({
      ...filterOptions,
    });
    filterModel = JSON.stringify({ items: systemAnswerArray });
  }

  // Construct filterForUrl object conditionally including filterModel only if it is defined.
  const filterForUrl = {
    detector_id: detectorId,
    ...(filterModel && { filterModel }), // Spread operator will include filterModel only if it's not undefined
  };

  let dataPageLink = generateUrlToDataPage(filterForUrl);

  if (additionalFilters) {
    dataPageLink = `${dataPageLink}${additionalFilters}`;
  }

  return dataPageLink;
};

export const removeEmptyValuesFromObject = (obj: Record<string, any>) =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => Boolean(value)));

export type FilterOptionsLabel = Exclude<LabelName, "Total Queries">;

export type FilterOptionsParams =
  | {
      period: MetricLimitMapping;
    }
  | {
      label: FilterOptionsLabel;
      answerType: AnswerType;
      period: MetricLimitMapping;
      value?: string;
      mode: DetectorModes;
    };

/**
 * This constant helper is because the value from metrics comes in upper case
 * but the backend expects lower case.
 */
const DETECTOR_MODES_WITH_LOWER_CASE_ANSWER_FILTER = [DetectorModes.BINARY];

export const getFilterOptions = (params: FilterOptionsParams) => {
  const result: Record<string, string> = {};

  if ("period" in params && params.period.filter) {
    result[params.period.filter.key] = params.period.filter.value;
  }

  if ("label" in params) {
    const { label, answerType, value, mode } = params;
    const isTotal = params.label === "Total Answered" && (value === "true" || value === "false");
    const answerKey = isTotal || label === "Not Answered" ? `is_${answerType}` : `${answerType}_str`;

    if (value) {
      if (
        (mode && DETECTOR_MODES_WITH_LOWER_CASE_ANSWER_FILTER.includes(mode)) ||
        value.toUpperCase() === ANSWER_VALUES.UNCLEAR
      )
        result[answerKey] = value.toLowerCase();
      else result[answerKey] = value;
    }

    if (label === "Answered By ML") {
      result[`${answerType}_source`] = LABEL_SOURCE[label];
    }

    if (label === "Answered By Cloud Labelers" || label === "Ground Truth") {
      const sourceKey = SOURCE_KEYS[answerType];
      result[sourceKey] = LABEL_SOURCE[label];
    }
  }

  return result;
};
